import { Identifiable } from '../../interface/identifiable';
import { Deletable } from '../../interface/deletable';
import { HasUserId } from '../../interface/has-user-id';
import { Factory } from '../../interface/factory';
import { CountryCode } from '../../enum/country-code';
import { Period, Period as ProductPeriod } from '../../enum/period';
import { HasTemplateId } from '../../interface/has-template-id';
import { Currency } from '../../enum/currency';
import { MothershipData } from './../mothership-data';
import { BeneficiaryType } from './beneficiary';
import { PaymentSpecification, PeriodicPaymentSpecification } from '../payment-specification';
import {
  Discount,
  DiscountPercentageVoucher,
  DiscountSinglePeriodPercentageVoucher,
  DiscountSinglePeriodPriceCampaign,
} from '../discount';
import Decimal from 'decimal.js';
import { AddonOwnership } from '../addon-ownership';
import { TerminationBehavior } from '../..';
import { ProductOwnerRef } from './product-owner';
import { CancellationReason } from '../cancellation-reason';
import { ProductSource } from '../../enum/product-source';

export class DegressiveValue {
  value: number;
  start: Date;
  end?: Date;

  constructor(json: any) {
    this.value = Number(json.value);
    this.start = new Date(json.start);
    this.end = json.end ? new Date(json.end) : undefined;
  }
}

export class ProductPostalAddress {
  address: string;
  city: string;
  postcode: string;
  country: CountryCode;
  buildingTypeCode: string;
  roomCount: number;
  areaSize: number;

  constructor(json: any) {
    this.address = json.address;
    this.city = json.city;
    this.postcode = json.postcode;
    this.country = json.country;
    this.buildingTypeCode = json.buildingTypeCode;
    this.roomCount = json.roomCount;
    this.areaSize = json.areaSize;
  }
}

export enum ApprovalProcessStatusType {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Blocked = 'Blocked',
  AwaitingDoubleCheck = 'AwaitingDoubleCheck',
  AwaitingCustomerApproval = 'AwaitingCustomerApproval',
  Completed = 'Completed',
  Rejected = 'Rejected',
}

export class ApprovalProcess {
  status: ApprovalProcessStatus;
  assignee?: string;
  trialVoucherId?: string;
  constructor(json: any) {
    this.status = new ApprovalProcessStatus(json.status);
    this.assignee = json.assignee;
    this.trialVoucherId = json.trialVoucherId ? json.trialVoucherId : undefined;
  }
}

export class ApprovalProcessStatus {
  type: ApprovalProcessStatusType;
  completedAt?: Date;
  sentAt?: Date;
  sentBy?: string;

  constructor(json: any) {
    this.type = json.type as ApprovalProcessStatusType;
    this.completedAt = json.completedAt ? new Date(json.completedAt) : undefined;
    this.sentAt = json.sentAt ? new Date(json.sentAt) : undefined;
    this.sentBy = json.sentBy ? json.sentBy : undefined;
  }
}

export class ProductAddon {
  id: string;
  templateIndex: number;
  count: number;
  ownership: AddonOwnership;

  constructor(json: any) {
    this.id = json.id;
    this.templateIndex = Number(json.templateIndex);
    this.count = Number(json.count);
    this.ownership = AddonOwnership.getFactory().make(json.ownership);
  }
}

export class RenewalOffer {
  price: number; // Price including discount.
  constructor(json: any) {
    this.price = Number(json.price);
  }
}

export enum ProductStatus {
  Tentative = 'Tentative',
  Ongoing = 'Ongoing',
  Cancelled = 'Cancelled',
  Terminated = 'Terminated',
  Pending = 'Pending',
  Rejected = 'Rejected',
  Trial = 'Trial',
}

export class ProductParent implements Identifiable {
  id: string;
  start: Date;
  constructor(json: any) {
    this.id = json.id;
    this.start = new Date(json.start);
  }
}

export enum PeriodType {
  Trial = 'Trial',
  Billable = 'Billable',
}

export class Periods {
  type: PeriodType;
  price: string;
  start: Date;
  end: Date;
  templateVersion: number;
  duration: Period;
  paymentSchedule: Period;

  constructor(json: any) {
    this.type = json.type;
    this.price = json.price;
    this.start = new Date(json.start);
    this.end = new Date(json.end);
    this.templateVersion = Number(json.templateVersion);
    this.duration = json.duration;
    this.paymentSchedule = json.paymentSchedule;
  }

  static getFactory(): Factory<Periods> {
    return new (class implements Factory<Periods> {
      make(json: any): Periods {
        switch (json.type) {
          case PeriodType.Trial:
            return new TrialPeriod(json);
          case PeriodType.Billable:
            return new BillablePeriod(json);
          default:
            throw new Error('Unrecognized Period type (' + json.type + ').');
        }
      }

      getTableName(): string {
        return 'Periods are never stored in IndexedDB.';
      }
    })();
  }
}

export class TrialPeriod extends Periods {
  terminationBehavior: TerminationBehavior;
  trialVoucherId: string;

  constructor(json: any) {
    super(json);
    this.terminationBehavior = json.terminationBehavior;
    this.trialVoucherId = json.trialVoucherId;
  }
}

export class BillablePeriod extends Periods {
  constructor(json: any) {
    super(json);
  }
}

export class Product implements Deletable, Identifiable, HasUserId, HasTemplateId {
  id: string;
  deleted?: boolean;
  deletedDbFlag: 0 | 1; // NOTE: Only used for indexing in browser DB.

  beneficiary: BeneficiaryType;

  aliasId?: number; // NOTE: Alias id is incremented from the top organization in the hierarchy.
  sequence?: number; // Current sequence of product period. (Can be calculated from start time.)

  countryCode: CountryCode; // Denormalized for db queries.
  issuingOrganizationIds: string[]; // NOTE: Lower index is higher in the hierarchy. Denormalized from template.

  templateId: string;
  templateVersion: number; // NOTE: The version number is 1 + the index in the array of the template.

  policyholder: ProductOwnerRef;
  parent?: ProductParent;

  beneficiaryId: string;
  addons: ProductAddon[];

  approvalProcess?: ApprovalProcess;
  degressiveValue?: DegressiveValue;

  currency: Currency; // Denormalized from template.
  price: number; // Price before discount. NOTE: Sent from the client as after discount.
  period: ProductPeriod;
  paymentSchedule: ProductPeriod;
  discountId?: string; // NOTE: Optional preferred discount for the next payment.
  netPremium: number;

  status: ProductStatus;
  periods: Periods[];

  start: Date;
  qualifyingTime: Date;
  end?: Date; // Do not renew after this date.

  paymentSpecifications: PaymentSpecification[];
  postalAddress?: ProductPostalAddress;

  renewalOffer?: RenewalOffer;

  mothershipData?: MothershipData;
  cancellationReason?: CancellationReason;

  /** Set on create; absent on older products. */
  source?: ProductSource;

  created: Date;
  modified: Date;

  constructor(json: any, beneficiary: BeneficiaryType) {
    this.id = json.id;
    this.deleted = json.deleted ? Boolean(json.deleted) : false;
    this.deletedDbFlag = json.deleted ? 1 : 0;

    this.beneficiary = beneficiary;

    this.aliasId = Number(json.aliasId) || undefined;
    this.sequence = 1;

    this.countryCode = json.countryCode as CountryCode;
    this.issuingOrganizationIds = json.issuingOrganizationIds ?? [];

    this.templateId = json.templateId;
    this.templateVersion = Number(json.templateVersion);

    this.policyholder = ProductOwnerRef.getFactory().make(json.policyholder);
    this.parent = json.parent ? new ProductParent(json.parent) : undefined;

    this.beneficiaryId = json.beneficiaryId;

    this.approvalProcess = json.approvalProcess ? new ApprovalProcess(json.approvalProcess) : undefined;
    this.degressiveValue = json.degressiveValue ? new DegressiveValue(json.degressiveValue) : undefined;

    this.postalAddress = json.postalAddress ? new ProductPostalAddress(json.postalAddress) : undefined;

    this.addons = json.addons ? json.addons.map((a: any) => new ProductAddon(a)) : [];

    this.currency = json.currency as Currency;
    this.price = Number(json.price);
    this.netPremium = Number(json.netPremium);
    this.period = json.period as ProductPeriod;
    this.paymentSchedule = json.paymentSchedule as ProductPeriod;
    this.discountId = json.discountId ? json.discountId : undefined;

    this.status = json.status as ProductStatus;

    this.start = new Date(json.start);
    this.qualifyingTime = new Date(json.qualifyingTime);
    this.end = json.end ? new Date(json.end) : undefined;

    this.periods = json.periods ? json.periods.map((p: any) => new Periods(p)) : [];
    this.addons = json.addons ? json.addons.map((a: any) => new ProductAddon(a)) : [];

    const psf = PaymentSpecification.getFactory();
    this.paymentSpecifications = json.paymentSpecifications
      ? json.paymentSpecifications.map((p: any) => psf.make(p))
      : [];

    this.renewalOffer = json.renewalOffer ? new RenewalOffer(json.renewalOffer) : undefined;

    this.mothershipData = json.mothershipData ? new MothershipData(json.mothershipData) : undefined;
    this.cancellationReason = json.cancellationReason ? new CancellationReason(json.cancellationReason) : undefined;

    this.source = json.source ? (json.source as ProductSource) : undefined;

    this.created = json.created ? new Date(json.created) : new Date();
    this.modified = json.modified ? new Date(json.modified) : new Date();
  }

  canStatusChange(): boolean {
    if (this.status === ProductStatus.Ongoing) {
      return true;
    }

    // Status of Cancelled products can be changed if the end date has not passed.
    if (this.status === ProductStatus.Cancelled && this?.end !== undefined) {
      if (this.end) {
        const now = new Date().getTime();
        const end = this.end.getTime();
        return now < end;
      }
    }

    return false;
  }

  hasExpired(): boolean {
    const now = new Date().getTime();
    const end = this.end?.getTime() ?? 0;
    return now > end;
  }

  hasNotExpired(): boolean {
    const now = new Date().getTime();
    const end = this.end?.getTime() ?? 0;
    return now < end;
  }

  isActive(): boolean {
    return (
      this.status === ProductStatus.Trial ||
      this.status === ProductStatus.Ongoing ||
      (this.status === ProductStatus.Cancelled && this.hasNotExpired()) ||
      (this.status === ProductStatus.Terminated && this.hasNotExpired())
    );
  }

  getLatestPeriod(): Periods {
    return this.periods[this.periods.length - 1];
  }

  getCurrentPeriod(): Periods {
    const now = new Date().getTime();
    for (const period of this.periods) {
      if (now >= period.start.getTime() && now <= period.end.getTime()) {
        return period;
      }
    }
    return this.getLatestPeriod();
  }

  /**
   * Billable insurance period that covers `date` (`start <= date < end`).
   * Same mapping as API `get_period`.
   */
  getPeriodContaining(date: Date): Periods | undefined {
    const timestamp = date.getTime();

    for (const period of this.periods) {
      if (period.type === PeriodType.Trial) {
        continue;
      }

      if (period.start.getTime() <= timestamp && timestamp < period.end.getTime()) {
        return period;
      }
    }

    return undefined;
  }

  hasNotStarted(): boolean {
    const now = new Date().getTime();
    const start = this.start.getTime();
    return now < start;
  }

  getLastPayment(): PaymentSpecification {
    return this.paymentSpecifications[this.paymentSpecifications.length - 1];
  }

  getLastPeriodicPaymentSpec(): PeriodicPaymentSpecification {
    return new PeriodicPaymentSpecification(this.getLastPayment());
  }

  public calculatePremium(
    product: TypeOfProduct,
    discount?: DiscountPercentageVoucher | DiscountSinglePeriodPercentageVoucher | DiscountSinglePeriodPriceCampaign,
  ) {
    let premium = 0;

    // Get the price from the latest period in product.
    const latestPeriod = product.getCurrentPeriod();
    const priceFromPeriod = +latestPeriod.price;

    premium += priceFromPeriod;

    if (!latestPeriod.price) {
      premium = product.price;
    }

    // Check if there is a discount
    if (discount && !this.isDiscountExpired(product, discount)) {
      if (discount instanceof DiscountPercentageVoucher || discount instanceof DiscountSinglePeriodPercentageVoucher) {
        const discountedPrice = new Decimal(premium)
          .mul(new Decimal(1).minus(new Decimal(discount.percent)))
          .toFixed(2, Decimal.ROUND_HALF_EVEN)
          .toString();

        premium = +discountedPrice;
      } else if (discount instanceof DiscountSinglePeriodPriceCampaign) {
        const discountedPrice = new Decimal(discount.price).toFixed(2, Decimal.ROUND_HALF_EVEN).toString();
        premium = +discountedPrice;
      }
    }

    if (new Decimal(premium).decimalPlaces() > 0) {
      return new Decimal(premium).toFixed(2, Decimal.ROUND_HALF_EVEN);
    } else if (new Decimal(premium).decimalPlaces() === 0) {
      return new Decimal(premium).toFixed(0, Decimal.ROUND_HALF_EVEN);
    } else {
      return new Decimal(premium).toFixed(0, Decimal.ROUND_HALF_EVEN);
    }
  }

  public calculateNextPremium(
    product: TypeOfProduct,
    discount?: DiscountPercentageVoucher | DiscountSinglePeriodPercentageVoucher | DiscountSinglePeriodPriceCampaign,
  ) {
    let premium = 0;

    // Get the price from the latest period in product.
    const currentPeriod = product.getCurrentPeriod();
    const latestPeriod = product.getLatestPeriod();

    if (currentPeriod === latestPeriod) {
      return undefined;
    }
    const priceFromPeriod = +latestPeriod.price;

    premium += priceFromPeriod;

    if (!latestPeriod.price) {
      premium = product.price;
    }

    // Check if there is a discount
    if (discount && !this.isDiscountExpired(product, discount)) {
      if (discount instanceof DiscountPercentageVoucher || discount instanceof DiscountSinglePeriodPercentageVoucher) {
        const discountedPrice = new Decimal(premium)
          .mul(new Decimal(1).minus(new Decimal(discount.percent)))
          .toFixed(2, Decimal.ROUND_HALF_EVEN)
          .toString();

        premium = +discountedPrice;
      } else if (discount instanceof DiscountSinglePeriodPriceCampaign) {
        const discountedPrice = new Decimal(discount.price).toFixed(2, Decimal.ROUND_HALF_EVEN).toString();
        premium = +discountedPrice;
      }
    }

    if (new Decimal(premium).decimalPlaces() > 0) {
      return new Decimal(premium).toFixed(2, Decimal.ROUND_HALF_EVEN);
    } else if (new Decimal(premium).decimalPlaces() === 0) {
      return new Decimal(premium).toFixed(0, Decimal.ROUND_HALF_EVEN);
    } else {
      return new Decimal(premium).toFixed(0, Decimal.ROUND_HALF_EVEN);
    }
  }

  public isDiscountExpired(product: TypeOfProduct, discount?: Discount): boolean {
    if (!discount) {
      return false;
    }

    const latestPeriod = product.getLatestPeriod();
    const periodStart = latestPeriod.start;
    if (!latestPeriod) {
      return false; // No period found, cannot determine discount validity
    }

    const now = new Date();

    if (discount) {
      if (discount instanceof DiscountPercentageVoucher) {
        if (
          discount.expiry &&
          discount.expiry.getTime() > periodStart.getTime() &&
          discount.expiry.getTime() > now.getTime()
        ) {
          return false; // Discount is valid
        } else if (!discount.expiry) {
          return false; // Discount is valid without expiry
        } else {
          return true; // Discount is expired
        }
      } else if (discount instanceof DiscountSinglePeriodPercentageVoucher) {
        if (
          discount.expiry &&
          discount.expiry.getTime() > periodStart.getTime() &&
          discount.expiry.getTime() > now.getTime()
        ) {
          return false; // Discount is valid
        } else if (!discount.expiry) {
          return false; // Discount is valid without expiry
        } else {
          return true; // Discount is expired
        }
      } else if (discount instanceof DiscountSinglePeriodPriceCampaign) {
        if (
          discount.expiry &&
          discount.expiry.getTime() > periodStart.getTime() &&
          discount.expiry.getTime() > now.getTime()
        ) {
          return false; // Discount is valid
        } else if (!discount.expiry) {
          return false; // Discount is valid without expiry
        } else {
          return true; // Discount is expired
        }
      }
    }
    return false; // No discount or unrecognized discount type
  }

  findProductSequence(
    product: Product,
    paymentSpec: PeriodicPaymentSpecification,
    parent?: Product,
  ): { sequence: number; periodStart: Date; periodEnd: Date } | undefined {
    const periodLength = this.getPeriodLength(product.period);
    let sequence = 1;
    let periodStart = new Date(product.start);
    let periodEnd = this.addPeriod(periodStart, periodLength);

    // Parse product end date if it exists
    const productEnd = product.end ? new Date(product.end) : null;

    // Extract payment start date
    const paymentStart = new Date(paymentSpec.from);

    if (parent) {
      let parentPeriodStart = new Date(parent.start);
      const parentPeriodLength = this.getPeriodLength(parent.period);

      // Align the parent's periods with the child's start date
      while (parentPeriodStart < periodStart) {
        parentPeriodStart = this.addPeriod(parentPeriodStart, parentPeriodLength);
        sequence++;
      }

      // Check if the parent's period aligns with the product's start date
      if (
        parentPeriodStart.getMonth() === periodStart.getMonth() &&
        parentPeriodStart.getDate() === periodStart.getDate()
      ) {
        periodStart = parentPeriodStart;
      } else {
        periodEnd = parentPeriodStart;
      }
    }

    if (!productEnd) {
      // Product without an end date
      while (periodEnd <= paymentStart) {
        periodStart = periodEnd;
        periodEnd = this.addPeriod(periodStart, periodLength);
        sequence++;
      }
    } else {
      // Product with an end date
      while (periodEnd <= paymentStart && periodEnd <= productEnd) {
        periodStart = periodEnd;
        periodEnd = this.addPeriod(periodStart, periodLength);
        sequence++;

        // If the new periodEnd exceeds productEnd, set it to productEnd and exit
        if (periodEnd > productEnd) {
          periodEnd = productEnd;
          break;
        }
      }

      // If paymentStart is after productEnd, ensure periodEnd doesn't exceed productEnd
      if (periodEnd > productEnd) {
        periodEnd = productEnd;
      }
    }

    return { sequence, periodStart, periodEnd };
  }

  // Helper function to add periods to a date based on the payment schedule
  private addPeriod(date: Date, periodLength: { months: number }): Date {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + periodLength.months);
    return newDate;
  }

  public hasStarted(): boolean {
    const now = new Date().getTime();
    const start = this.qualifyingTime.getTime();
    return now >= start;
  }

  private subtractPeriod(date: Date, periodLength: { months: number }): Date {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() - periodLength.months);
    return newDate;
  }

  // Helper function to determine the length of periods
  private getPeriodLength(period: ProductPeriod): { months: number } {
    switch (period) {
      case ProductPeriod.Day:
        return { months: 0 }; // Not handling days for simplicity
      case ProductPeriod.Month:
        return { months: 1 };
      case ProductPeriod.QuarterYear:
        return { months: 3 };
      case ProductPeriod.HalfYear:
        return { months: 6 };
      case ProductPeriod.Year:
        return { months: 12 };
      default:
        throw new Error('Unsupported period type');
    }
  }

  public static getFactory(): Factory<Product> {
    return new (class implements Factory<Product> {
      make(json: any): Product {
        switch (json.beneficiary) {
          case BeneficiaryType.User:
            return new UserProduct(json);
          case BeneficiaryType.Object:
            return new ObjectProduct(json);
          default:
            throw new Error('Unrecognized product beneficiary (' + json.beneficiary + ').');
        }
      }

      getTableName(): string {
        return 'products';
      }
    })();
  }

  static getUrl(countryCode: CountryCode, productId?: string): string;
  static getUrl(countryCode: CountryCode, productId: string): string {
    return '/countries/' + countryCode + '/products' + (productId ? '/' + productId : '');
  }

  static getRegretUrl(countryCode: CountryCode, productId: string): string {
    return Product.getUrl(countryCode, productId) + '/regret';
  }

  static getRegretPreviewUrl(countryCode: CountryCode, productId: string): string {
    return Product.getRegretUrl(countryCode, productId) + '/preview';
  }
}

export class UserProduct extends Product {
  constructor(json: any) {
    super(json, BeneficiaryType.User);
  }
}

export class ObjectProduct extends Product {
  constructor(json: any) {
    super(json, BeneficiaryType.Object);
  }
}

type TypeOfProduct = UserProduct | ObjectProduct;
